(() => {
  if (customElements.get('hedera-stock-counter')) return;

  const quantity = (value) => Math.max(0, Math.floor(Number(value) || 0));
  const cartQuantities = (cart) => {
    const totals = new Map();
    for (const item of cart.items) {
      const id = String(item.variant_id ?? item.id);
      totals.set(id, (totals.get(id) || 0) + quantity(item.quantity));
    }
    return totals;
  };
  const fetchWithTimeout = async (url, kind) => {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 10000);
    try {
      const response = await fetch(url, {
        signal: controller.signal,
        credentials: 'same-origin',
        cache: 'no-store',
        headers: { Accept: kind === 'json' ? 'application/json' : 'text/html' }
      });
      if (!response.ok) throw new Error('Stock refresh unavailable');
      return await response[kind]();
    } finally {
      clearTimeout(timeout);
    }
  };

  const manager = {
    nodes: new Set(),
    totals: new Map(),
    knownCart: false,
    lastRead: 0,
    revision: 0,
    pending: false,
    running: false,
    refreshAll: false,
    variants: new Set(),
    register(node) {
      this.nodes.add(node);
      node.render(this.knownCart ? (this.totals.get(node.variantId) || 0) : quantity(node.dataset.inCart));
      if (!this.knownCart || Date.now() - this.lastRead > 10000) this.request();
    },
    request({ variantIds = [], all = false } = {}) {
      for (const id of variantIds) if (id != null && id !== '') this.variants.add(String(id));
      this.refreshAll ||= all;
      this.pending = true;
      this.revision += 1;
      clearTimeout(this.timer);
      this.timer = setTimeout(() => this.flush(), 60);
    },
    async flush() {
      if (this.running) return;
      this.running = true;
      try {
        while (this.pending) {
          this.pending = false;
          if (!this.nodes.size) {
            this.knownCart = false;
            this.variants.clear();
            this.refreshAll = false;
            continue;
          }
          const revision = this.revision;
          const ids = new Set(this.variants);
          const all = this.refreshAll;
          this.variants.clear();
          this.refreshAll = false;
          this.nodes.forEach((node) => node.setAttribute('aria-busy', 'true'));
          try {
            const root = window.Shopify?.routes?.root || '/';
            const cart = await fetchWithTimeout(`${root}cart.js`, 'json');
            if (!Array.isArray(cart.items) || !Number.isFinite(cart.item_count)) throw new Error('Invalid cart response');
            const nextTotals = cartQuantities(cart);
            for (const id of new Set([...this.totals.keys(), ...nextTotals.keys()])) {
              if (this.totals.get(id) !== nextTotals.get(id)) ids.add(id);
            }
            const groups = new Map();
            for (const node of this.nodes) {
              if (!node.isConnected || !(all || ids.has(node.variantId) || node.failed)) continue;
              const url = new URL(node.dataset.productUrl, location.origin);
              if (url.origin !== location.origin) continue;
              url.searchParams.set('variant', node.variantId);
              url.searchParams.set('section_id', 'hedera-stock-data');
              const previewTheme = new URL(location.href).searchParams.get('preview_theme_id');
              if (previewTheme) url.searchParams.set('preview_theme_id', previewTheme);
              groups.set(url.toString(), node.variantId);
            }
            const snapshots = new Map();
            const queue = [...groups];
            const worker = async () => {
              while (queue.length) {
                const [url, id] = queue.shift();
                try {
                  const html = await fetchWithTimeout(url, 'text');
                  const doc = new DOMParser().parseFromString(html, 'text/html');
                  if (!doc.querySelector('[data-hd-stock-response]')) throw new Error('Missing stock section');
                  const data = doc.querySelector('[data-hd-stock-snapshot]');
                  if (!data) {
                    snapshots.set(id, { disabled: true });
                  } else {
                    const inventory = Number(data.dataset.inventory);
                    if (data.dataset.variantId !== id || !Number.isFinite(inventory)) throw new Error('Invalid stock response');
                    snapshots.set(id, { inventory: quantity(inventory) });
                  }
                } catch {
                  snapshots.set(id, { failed: true });
                }
              }
            };
            await Promise.all(Array.from({ length: Math.min(3, queue.length) }, worker));
            if (revision !== this.revision) {
              for (const id of ids) this.variants.add(id);
              this.refreshAll ||= all;
              continue;
            }
            this.totals = nextTotals;
            this.knownCart = true;
            this.lastRead = Date.now();
            for (const node of this.nodes) {
              const snapshot = snapshots.get(node.variantId);
              if (snapshot) {
                node.failed = Boolean(snapshot.failed || snapshot.disabled);
                if (snapshot.inventory !== undefined) node.dataset.inventory = String(snapshot.inventory);
              }
              node.render(this.totals.get(node.variantId) || 0);
            }
          } catch {
            if (revision === this.revision) {
              this.knownCart = false;
              for (const node of this.nodes) {
                node.failed = true;
                node.hidden = true;
              }
            }
          } finally {
            if (revision === this.revision) this.nodes.forEach((node) => node.removeAttribute('aria-busy'));
          }
        }
      } finally {
        this.running = false;
      }
    }
  };

  class HederaStockCounter extends HTMLElement {
    connectedCallback() {
      this.variantId = this.dataset.variantId;
      this.failed = false;
      manager.register(this);
    }
    disconnectedCallback() {
      manager.nodes.delete(this);
    }
    remainingLabel(count) {
      if (count === 1) return this.dataset.labelOne;
      if (count === 2 && this.dataset.language === 'ar') return this.dataset.labelTwo;
      if (this.dataset.language === 'ar' && count % 100 >= 3 && count % 100 <= 10) return this.dataset.labelFew;
      return this.dataset.labelOther;
    }
    render(inCart) {
      const inventory = quantity(this.dataset.inventory);
      const remaining = Math.max(0, inventory - inCart);
      const threshold = quantity(this.dataset.threshold);
      this.hidden = this.failed || (this.dataset.display === 'low' && inventory > threshold);
      this.classList.toggle('hd-stock-counter--low', inventory > 0 && inventory <= threshold);
      const label = inventory === 0
        ? this.dataset.soldLabel
        : remaining === 0
          ? this.dataset.allLabel
          : this.remainingLabel(remaining).replaceAll('__COUNT__', String(remaining));
      const output = this.querySelector('[data-hd-stock-label]');
      if (output && output.textContent !== label) output.textContent = label;
      const cartOutput = this.querySelector('[data-hd-stock-cart]');
      if (cartOutput) {
        const cartLabel = this.dataset.cartLabel.replaceAll('__COUNT__', String(inCart));
        if (cartOutput.textContent !== cartLabel) cartOutput.textContent = cartLabel;
        cartOutput.hidden = inCart === 0 || this.dataset.showCart === 'false';
      }
    }
  }

  customElements.define('hedera-stock-counter', HederaStockCounter);
  document.addEventListener('hedera:cart-updated', (event) => manager.request(event.detail || {}));
  document.addEventListener('hedera:cart:discount-updated', () => manager.request({ all: true }));
  window.addEventListener('pageshow', (event) => {
    if (event.persisted) manager.request({ all: true });
  });
  document.addEventListener('visibilitychange', () => {
    if (!document.hidden && Date.now() - manager.lastRead > 30000) manager.request({ all: true });
  });
  window.hederaStockCounter = { refresh: () => manager.request({ all: true }) };
})();
